import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Trophy } from "lucide-react";

// Set to false to restore the previous color of positions below the podium.
const ENABLE_LIGHT_NON_PODIUM_CARDS = true;
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => String(CURRENT_YEAR - 3 + i));

export default function Ranking() {
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    api.get("/ranking", { params: { year } })
      .then(r => { if (active) setItems(r.data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [year]);
  const hasResults = items.some(a => a.attendance_total > 0 || a.competitions > 0);
  return (
    <AppLayout kind="athlete">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold mb-2">Ranking GSX — {year}</h1>
          <p className="text-slate-400">Classificação interna baseada em competições e frequência.</p>
        </div>
        <label className="text-xs text-amber-300">Ano
          <select aria-label="Ano do ranking" value={year} onChange={event => setYear(event.target.value)} className="block mt-1 rounded-md border border-amber-500/30 bg-[#0D2A52] px-3 py-2 text-white">
            {YEARS.map(value => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
      </div>
      {loading && <p className="text-sm text-slate-400">Carregando ranking…</p>}
      {!loading && error && <p role="alert" className="text-sm text-amber-200">Não foi possível consultar o ranking. Tente novamente mais tarde.</p>}
      {!loading && !error && !hasResults && <p className="text-sm text-slate-400">Ainda não há frequência ou competições registradas em {year}.</p>}
      {!loading && !error && hasResults &&
      <div className="space-y-3">
        {items.map((a, i) => {
          const src = a.avatar_file_id
            ? `${API}/athletes/${a.id}/avatar?v=${encodeURIComponent(a.avatar_uploaded_at || a.avatar_file_id)}`
            : null;
          return (
            <Card
              key={a.id}
              className={`bg-transparent gsx-card-dark gsx-ranking-card p-4 flex items-center justify-between flex-wrap gap-3${ENABLE_LIGHT_NON_PODIUM_CARDS && i >= 3 ? " gsx-ranking-card--soft" : ""}`}
              data-medal={i === 0 ? "gold" : i === 1 ? "silver" : i === 2 ? "bronze" : undefined}
              data-testid={`ranking-row-${a.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-mono font-bold ${
                  i === 0 ? "bg-amber-500 text-[#071A33]" :
                  i === 1 ? "bg-slate-300 text-[#071A33]" :
                  i === 2 ? "bg-amber-700 text-white" :
                  "bg-white/5 text-slate-300 border border-white/10"
                }`}>
                  {i + 1}
                </div>
                <div className="w-10 h-10 rounded-full border border-amber-500/30 bg-amber-500/10 overflow-hidden flex items-center justify-center shrink-0">
                  {src ? (
                    <img src={src} alt={a.name} className="w-full h-full object-cover" data-testid={`ranking-avatar-${a.id}`} />
                  ) : (
                    <span className="text-amber-300 font-semibold">{(a.name||"?").slice(0,1)}</span>
                  )}
                </div>
                <div>
                  <div className="text-white font-medium flex items-center gap-2">{a.name} {i < 3 && <Trophy className="w-4 h-4 text-amber-400" />}</div>
                  <div className="text-xs text-slate-400">{a.category} • CBX {a.rating_cbx || "—"}</div>
                </div>
              </div>
              <div className="flex gap-6 text-xs">
                <div><div className="text-slate-400">Pontos</div><div className="font-mono text-amber-400 text-lg">{a.points}</div></div>
                <div><div className="text-slate-400">Comp.</div><div className="font-mono text-white">{a.competitions}</div></div>
                <div><div className="text-slate-400">Freq.</div><div className="font-mono text-white">{a.frequency}%</div></div>
              </div>
            </Card>
          );
        })}
      </div>
      }
    </AppLayout>
  );
}
